import { attr, css, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { CompanysTemplate } from './company.template';
import { EditCompany } from './edit';
import {
  DatasourceConfiguration,
  EntityManagement,
} from '@genesislcap/foundation-entity-management';
import { CASCADE_ENUM, IEnumService } from '../../../services/enum.service';
import { TrueOrFalseCellRenderer } from '../../../utils/custom-renderer/true-or-false-icon-cell-renderer';
import { convertDateToUTC } from '../../../utils/formatting';
import { IUserService } from '../../../services/user.service';
import { PROFILE_RIGHTS } from '../../../utils';

EditCompany;

export enum Permissions {
  add,
  edit,
  delete,
}

const defaultColumnConfig = {
  enableCellChangeFlash: true,
  enableRowGroup: true,
  enablePivot: true,
  enableValue: true,
};

export function CompanyColumnConfig(context: Companys) {
  return [
    {
      ...defaultColumnConfig,
      field: 'NAME',
      headerName: 'Company Name',
      minWidth: '120',
    },
    {
      ...defaultColumnConfig,
      field: 'COMPANY_ROLE',
      headerName: 'Company Role',
      minWidth: '120',
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.COMPANY_ROLE, params.data.COMPANY_ROLE),
    },
    {
      ...defaultColumnConfig,
      field: 'IS_SPV_FLAG',
      headerName: 'SPV ?',
      minWidth: '120',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.IS_SPV_FLAG,
    },
    {
      ...defaultColumnConfig,
      field: 'CASCADE_PARENT_COMPANY_NAME',
      headerName: ' Parent Legal Entity/ Corporate Parent',
      minWidth: '120',
    },
    {
      ...defaultColumnConfig,
      field: 'COMPANY_LEI',
      headerName: 'Company LEI',
      minWidth: '120',
    },
    {
      ...defaultColumnConfig,
      field: 'REGULATORY_STATUS',
      headerName: 'Regulatory Status',
      minWidth: '140',
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.REGULATORY_STATUS,
          params.data.REGULATORY_STATUS,
        ),
    },
    {
      ...defaultColumnConfig,
      field: 'PHONE_NUMBER',
      headerName: 'Company Phone Number',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'ADDRESS_STREET',
      headerName: 'Company Address Street',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'CITY_NAME',
      headerName: 'Company Address City',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'STATE_NAME',
      headerName: 'Company Address State',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'ADDRESS_ZIP_CODE',
      headerName: 'Company Address Zip Code',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'COUNTRY_NAME',
      headerName: 'Company Country',
      minWidth: '120',
    },
    {
      ...defaultColumnConfig,
      field: 'FEDERAL_ENTITY_INDICATOR',
      headerName: 'Federal Entity Indicator',
      minWidth: '180',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.FEDERAL_ENTITY_INDICATOR,
    },
    {
      ...defaultColumnConfig,
      field: 'FINANCIAL_ENTITY_INDICATOR',
      headerName: 'Financial Entity Indicator',
      minWidth: '180',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.FINANCIAL_ENTITY_INDICATOR,
    },
    {
      ...defaultColumnConfig,
      field: 'RFQ_NEGOTIATION_FLAG',
      headerName: 'RFQ Negotiation',
      minWidth: '120',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.RFQ_NEGOTIATION_FLAG,
    },
    {
      ...defaultColumnConfig,
      field: 'AUTO_EXECUTION_FLAG',
      headerName: 'Auto-Execute?',
      minWidth: '120',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.AUTO_EXECUTION_FLAG,
    },
    {
      ...defaultColumnConfig,
      field: 'CREDITWORTHY_INSTITUTION',
      headerName: 'Creditworthy Institution?',
      minWidth: '140',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.CREDITWORTHY_INSTITUTION,
    },
    {
      ...defaultColumnConfig,
      field: 'PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION',
      headerName: 'Parent Guarantee from Creditworthy Institution?',
      minWidth: '180',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION,
    },
    {
      ...defaultColumnConfig,
      field: 'POST_LETTERS_OF_CREDIT',
      headerName: 'Post Letters of Credit?',
      minWidth: '180',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.POST_LETTERS_OF_CREDIT,
    },
    {
      ...defaultColumnConfig,
      field: 'SEF_PARTICIPANT_AGREEMENT_CONSENT',
      headerName: 'SEF Agreement Signed?',
      minWidth: '180',
      cellRenderer: TrueOrFalseCellRenderer,
      cellRendererParams: (params) => params.data.SEF_PARTICIPANT_AGREEMENT_CONSENT,
    },
    {
      ...defaultColumnConfig,
      field: 'SEF_PARTICIPANT_AGREEMENT_CONSENT_DATETIME',
      headerName: 'SEF Agreement Timestamp',
      valueFormatter: (params) =>
        params.data && params.data.SEF_PARTICIPANT_AGREEMENT_CONSENT_DATETIME
          ? new Date(params.data.SEF_PARTICIPANT_AGREEMENT_CONSENT_DATETIME).toLocaleString()
          : '-',
      minWidth: '180',
    },
    {
      ...defaultColumnConfig,
      field: 'NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE',
      headerName: 'Physical Platform Access Agreement Date',
      valueFormatter: (params) =>
        params.data && params.data.NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE
          ? convertDateToUTC(
              new Date(params.data.NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE),
            ).toLocaleDateString()
          : '-',
      minWidth: '180',
    },
  ];
}

@customElement({
  name: 'company-management',
  template: CompanysTemplate,
  styles: css`
    :host {
      height: 100%;
    }

    .link-advisors {
      position: absolute;
      top: 8px;
      right: 60px;
    }

    .create-company {
      position: absolute;
      top: 8px;
      right: 0px;
    }
  `,
})
export class Companys extends EntityManagement {
  @observable datasourceConfig: DatasourceConfiguration;
  @attr resourceName = 'ALL_COMPANYS';
  @observable companyEntityElm: EntityManagement;

  @observable columns: any = CompanyColumnConfig(this);
  @observable permissions: Permissions[] = [];
  @IEnumService enumService: IEnumService;
  @IUserService userService: IUserService;
  @observable isAdmin: boolean = false;

  @attr isEditable = true;

  @observable gridOptions = {
    scrollbarWidth: 10,
  };

  @observable linkAdvisors: boolean = false;

  constructor() {
    super();

    if (this.companyEntityElm) {
      const modal = this.companyEntityElm.shadowRoot.querySelector('zero-modal');
      if (modal) {
        modal.classList.add('company-entity-modal');
      }
    }
    this.isAdmin = this.userService
      .getFilteredPermissions()
      .includes(PROFILE_RIGHTS.ADMIN_VISIBILITY);
  }

  public openLinkAdvisors() {
    this.linkAdvisors = true;
  }

  public createCompany() {
    this.companyEntityElm.editModalVisible = true;
  }
}
